import { useState } from "react";
import { useForm } from "react-hook-form";
import { KeyValue, KeyValueDefault } from "@/main/models/common";
import { supplierApi } from "@/main/api";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import {
	SaveSupplierCompanyRelationRequest,
	SupplierFilterRequest,
	SupplierInfoList,
} from "@/main/models/request/supplierRequest";
import { SupplierRegistrationDataResponse } from "@/main/models/response";
import { getDefaultMessageResultsFound, getTranslateByKey, removeEmptyValues } from "@/main/services/utils";
import { convertStringForBoolean } from "@/main/services/utilsString";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { makeStyles } from "@mui/styles";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { toast } from "react-toastify";
import KogTextAreaControlledNew from "../../textArea/textArea";
import KogAutoCompleteControlled from "../../inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "../../inputText/inputTextControlledNew";
import KOGBoxGridCheckboxInverted from "../../boxGridCheckboxInverted/boxGridCheckboxInverted";
import * as S from "./styles";
import { KOGAreaButtons } from "../../styles";

const useStyles = makeStyles(() => ({
	header: {
		backgroundColor: "#EAEAEA",
	},
	bodyFilter: {
		backgroundColor: "#EAEAEA",
	},
	bodyRegister: {
		backgroundColor: "#EAEAEA",
	},
	detail: {
		display: "flex",
		flexDirection: "column",
	},
}));

interface Props {
	open?: any;
	closeModal?: any;
	reasonReducer?: any;
	distributionRegionReducer?: any;
	supplierTypeReducer?: any;
	manufacturerReducer?: any;
	watcherCountry?: any;
	guidBodyshop?: any;
	guidInsure?: any;
	resetGrid?: any;
}

const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};

interface IGridData {
	data: SupplierRegistrationDataResponse[];
	totalItems: number;
	isLoading: boolean;
}

export interface ISearchRestrictedAndFavoriteForm {
	supplierName: string | null;
	rfc: string | null;
	supplierType: KeyValue | null;
	make: KeyValue | null;
	region: KeyValue | null;
	reason: KeyValue | null;
	observation: string | null;
}

function BlackListRegister({
	open,
	closeModal,
	reasonReducer,
	distributionRegionReducer,
	supplierTypeReducer,
	manufacturerReducer,
	watcherCountry,
	guidBodyshop,
	guidInsure,
	resetGrid,
}: Props) {
	const classes = useStyles();
	const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);
	const [isAccordionFilterOpen, setIsAccordionFilterOpen] = useState(true);
	const [isAccordionRegisterOpen, setIsAccordionRegisterOpen] = useState(false);
	const [selectedReason, setSelectedReason] = useState<KeyValueDefault | null>(null);
	const [selectedItems, setSelectedItems] = useState<SupplierInfoList[]>([]);
	const [formFilters, setFormFilters] = useState<SupplierFilterRequest | null>(null);
	const [hasShowMore, setHasShowMore] = useState<boolean>(false);
	const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<boolean>(false);

	const INITIAL_FORM_STATE_SEARCH = {
		supplierName: "",
		rfc: "",
		supplierType: null as KeyValue | null,
		make: null as KeyValue | null,
		region: null as KeyValue | null,
	};
	type IBlacklistFormSearch = typeof INITIAL_FORM_STATE_SEARCH;
	const methodSearch = useForm<IBlacklistFormSearch>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE_SEARCH,
	});

	const INITIAL_FORM_STATE_SAVE = {
		reason: null as KeyValue | null,
		observation: "",
	};
	type IBlacklistFormSave = typeof INITIAL_FORM_STATE_SAVE;
	const methodSave = useForm<IBlacklistFormSave>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE_SAVE,
	});

	const rowHeaders = [`${getTranslateByKey("information.rfc")} / ${getTranslateByKey("screen.supplier")}`];

	const isDisabledObservation = () => convertStringForBoolean(selectedReason?.default);

	function onSubmitSearch(formInputs: ISearchRestrictedAndFavoriteForm) {
		setGridData({
			isLoading: true,
			totalItems: 0,
			data: [],
		});

		const requestForm: SupplierFilterRequest = {
			guidCountry: String(watcherCountry.key),
			guidBodyshop: String(guidBodyshop) ? String(guidBodyshop) : null,
			guidInsurer: String(guidInsure) ? String(guidInsure) : null,
			companyName: formInputs.supplierName,
			registrationNumber: formInputs.rfc,
			idSupplierType: formInputs.supplierType?.key ? Number(formInputs.supplierType?.key) : null,
			guidVehicleManufacturer: formInputs.make?.key ? String(formInputs.make?.key) : null,
			guidDistributionRegion: formInputs.region?.key ? String(formInputs.region?.key) : null,
			type: HeadQuarterBranchFilterEnum.All,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize15,
		};

		const requestFormatted = removeEmptyValues(requestForm);
		setFormFilters(requestFormatted);
		supplierApi.getGridRestrictedAndFavorite(requestFormatted).then((response) => {
			setGridData({
				isLoading: false,
				totalItems: response.data.totalNumberOfRecords,
				data: response.data.results,
			});
			setHasShowMore(response.data.results.length < response.data.totalNumberOfRecords);

			setPageIndex(PaginationEnum.PageIndexIntial);
			setIsAccordionRegisterOpen(true);
			setIsAccordionFilterOpen(false);
		});
	}

	function onLoadInfiniteScroll(request: SupplierFilterRequest) {
		setIsLoadingInfiniteScroll(true);
		supplierApi
			.getGridRestrictedAndFavorite(request)
			.then((response) => {
				let resultData: SupplierRegistrationDataResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data && response.data.results && response.data.results.length > 0) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}
				if (gridData.data.length > 0) {
					resultData = [...gridData.data, ...resultData];
				}
				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					data: resultData,
				});

				// Disable show more
				if (resultTotalItems < PaginationEnum.PageSize15 || resultTotalItems === resultData.length) {
					setHasShowMore(false);
				}
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function handleClear() {
		methodSearch.reset(INITIAL_FORM_STATE_SEARCH);
		methodSearch.setValue("supplierName", "");
		methodSearch.setValue("rfc", "");
		methodSearch.setValue("supplierType", null);
		methodSearch.setValue("make", null);
		methodSearch.setValue("region", null);
		methodSave.setValue("reason", null);
		methodSave.setValue("observation", "");
		setGridData(INITIAL_GRID_STATE);
		setFormFilters(null);
		setPageIndex(PaginationEnum.PageIndexIntial);
		setHasShowMore(false);
		setIsLoadingInfiniteScroll(false);
	}

	async function onSubmitSave(formInputs: ISearchRestrictedAndFavoriteForm) {
		if (selectedItems.length > 0) {
			const resultConfirm = await getConfirmationReason(
				getTranslateByKey("information.confirmChangeTitle"),
				ReasonTypeEnum.ChangeCompany,
			);

			if (!resultConfirm.confirmed) return;

			let filesRequest = undefined;
			if (resultConfirm.files && resultConfirm.files.length > 0) {
				filesRequest = await Promise.all(
					resultConfirm.files.map(async (item) => {
						const base64DataUrl = await toBase64(item);
						return {
							fileName: item.name,
							contentType: item.type,
							fileBase64: base64DataUrl as string,
						};
					}),
				);
			}

			const requestFormatted: SaveSupplierCompanyRelationRequest = {
				supplierInfoList: selectedItems,
				guidBodyShop: guidBodyshop || null,
				guidInsurer: guidInsure || null,
				guidReason: (formInputs.reason?.key ?? undefined) as string | undefined,
				reasonDescription: String(formInputs.observation),
				favorite: false,
				restricted: true,
				changeJustification: {
					guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
					reasonDescription: resultConfirm?.observation || null,
					filesUpload: filesRequest,
				},
			};

			supplierApi
				.saveSupplierCompanyRelation(requestFormatted)
				.then(() => {
					resetGrid();
					toast.success(getTranslateByKey("information.blacklistSavedSuccessfully"));
					closeModal();
					handleClear();
				})
				.catch((_) => {});
		}
	}

	return (
		<>
			{open && (
				<KogModalSideComponent
					width="600px"
					open={open}
					closeModal={() => closeModal()}
					id="id-blacklist-modal-scroll"
					titleModalReducer={getTranslateByKey("information.registerBlacklist")}
					title={<S.Title>{getTranslateByKey("information.registerBlacklist")}</S.Title>}
				>
					<Accordion
						expanded={isAccordionFilterOpen}
						onChange={(event, isExpanded) => setIsAccordionFilterOpen(isExpanded)}
						className={classes.bodyFilter}
						elevation={0}
					>
						<AccordionSummary
							className={classes.header}
							expandIcon={<S.IconExpandMore />}
							aria-controls="panel1bh-content"
							id="panel1bh-header"
						>
							<S.AccordionTitle>{getTranslateByKey("information.filters")}</S.AccordionTitle>
						</AccordionSummary>
						<AccordionDetails>
							<form onSubmit={methodSearch.handleSubmit(onSubmitSearch)} noValidate style={{ width: "100%" }}>
								<KogInputTextControlledNew
									name="supplierName"
									id="id-blacklistRegister-text-supplierName"
									label={getTranslateByKey("information.supplierName")}
									control={methodSearch.control}
									style={{ marginBottom: 10 }}
								/>
								<KogInputTextControlledNew
									name="rfc"
									id="id-blacklistRegister-text-rfc"
									label={getTranslateByKey("information.rfc")}
									control={methodSearch.control}
									style={{ marginBottom: 10 }}
								/>
								<KogAutoCompleteControlled
									className="ignore-click"
									id="id-blacklistRegister-dropdown-supplierType"
									label={getTranslateByKey("information.supplierType")}
									nameCustom="supplierType"
									errorMessageCustom=""
									control={methodSearch.control}
									setValueCustom={methodSearch.setValue}
									options={supplierTypeReducer.supplierType.data}
									isLoading={supplierTypeReducer.supplierType.isLoading}
									rulesCustom={{ required: false }}
									hasErrorCustom={false}
									onChangeCustom={() => {}}
									style={{ marginBottom: 10 }}
								/>
								<KogAutoCompleteControlled
									className="ignore-click"
									id="id-blacklistRegister-dropdown-make"
									label={getTranslateByKey("information.make")}
									nameCustom="make"
									errorMessageCustom=""
									control={methodSearch.control}
									setValueCustom={methodSearch.setValue}
									options={manufacturerReducer.manufacturers.data}
									isLoading={manufacturerReducer.manufacturers.isLoading}
									rulesCustom={{ required: false }}
									hasErrorCustom={false}
									onChangeCustom={() => {}}
									style={{ marginBottom: 10 }}
								/>
								<KogAutoCompleteControlled
									className="ignore-click"
									id="id-blacklistRegister-dropdown-regionName"
									label={getTranslateByKey("information.regionName")}
									nameCustom="region"
									errorMessageCustom=""
									control={methodSearch.control}
									setValueCustom={methodSearch.setValue}
									options={distributionRegionReducer.distributionRegion.data}
									isLoading={distributionRegionReducer.distributionRegion.isLoading}
									rulesCustom={{ required: false }}
									hasErrorCustom={false}
									onChangeCustom={() => {}}
									style={{ marginBottom: 10 }}
								/>
								<KOGAreaButtons>
									<SecondaryButton id="id-blacklistRegister-button-clear" onClick={handleClear}>
										{getTranslateByKey("information.clear")}
									</SecondaryButton>
									<SecondaryButton type="submit" id="id-blacklistRegister-button-search">
										{getTranslateByKey("information.search")}
									</SecondaryButton>
								</KOGAreaButtons>
							</form>
						</AccordionDetails>
					</Accordion>
					<Accordion
						expanded={isAccordionRegisterOpen}
						onChange={(event, isExpanded) => setIsAccordionRegisterOpen(isExpanded)}
						className={classes.bodyRegister}
						elevation={0}
					>
						<AccordionSummary
							className={classes.header}
							expandIcon={gridData.isLoading ? <S.Spinner /> : <S.IconExpandMore />}
							aria-controls="panel2bh-content"
							id="panel2bh-header"
						>
							<S.AccordionTitle>{getTranslateByKey("information.register")}</S.AccordionTitle>
						</AccordionSummary>
						<AccordionDetails className={classes.detail}>
							<S.TitleGrid>{titleGrid}</S.TitleGrid>
							<form onSubmit={methodSave.handleSubmit(onSubmitSave)} noValidate>
								{gridData.totalItems > 0 && (
									<>
										<KOGBoxGridCheckboxInverted
											isLoadingInfiniteScroll={isLoadingInfiniteScroll}
											pageIndex={pageIndex}
											setPageIndex={setPageIndex}
											hasShowMore={hasShowMore}
											formFilters={formFilters}
											setFormFilters={setFormFilters}
											getGridData={(request: SupplierFilterRequest) => {
												onLoadInfiniteScroll(request);
											}}
											rowHeaders={rowHeaders}
											options={gridData.data}
											optionsSelected={selectedItems}
											setOptionsSelected={setSelectedItems}
											onChangeCustom={(optionsSelected) => {
												setSelectedItems(optionsSelected);
											}}
											idScrollTargetGrid="id-blacklist-modal-scroll"
										/>
										<KogAutoCompleteControlled
											className="ignore-click"
											id="id-blacklistRegister-text-reason"
											required
											label={getTranslateByKey("information.reason")}
											nameCustom="reason"
											errorMessageCustom=""
											control={methodSave.control}
											setValueCustom={methodSave.setValue}
											options={reasonReducer.reason.data}
											isLoading={reasonReducer.reason.isLoading}
											rulesCustom={{ required: true }}
											hasErrorCustom={false}
											onChangeCustom={(optionSelected: KeyValueDefault | null) => {
												setSelectedReason(optionSelected);
												methodSave.setValue("reason", optionSelected);
											}}
											style={{ marginTop: 10, marginBottom: 10 }}
										/>
										{isDisabledObservation() && (
											<KogTextAreaControlledNew
												id="id-blacklistRegister-text-observation"
												name="observation"
												required
												control={methodSave.control}
												errorMessageCustom={
													methodSave.formState.errors.observation?.type === "required"
														? getTranslateByKey("information.observationRequired")
														: ""
												}
												rulesCustom={{
													required: isDisabledObservation(),
												}}
												label={getTranslateByKey("information.observation")}
											/>
										)}
										<div className="d-flex justify-content-center" style={{ marginTop: 10 }}>
											<SecondaryButton
												id="id-blacklistRegister-button-save"
												type="submit"
												disabled={!(selectedItems.length > 0)}
											>
												{getTranslateByKey("information.save")}
											</SecondaryButton>
										</div>
									</>
								)}
							</form>
						</AccordionDetails>
					</Accordion>
				</KogModalSideComponent>
			)}
			<ModalConfirmationReason closeByOverlay={false} />
		</>
	);
}

export default BlackListRegister;
